<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    title="Štampanje žreba"
    width="600px"
    @close="handleClose"
  >
    <div class="print-modal-content">
      <div class="step-content">
        <h3 class="step-title">Izaberite kategorije:</h3>
        <div class="selection-options">
          <el-checkbox
            v-model="selectAll"
            :indeterminate="isIndeterminate"
            @change="handleSelectAllChange"
            class="select-all-checkbox"
          >
            Izaberi sve
          </el-checkbox>
          <div class="category-list">
            <div
              v-for="category in categoriesWithDisciplines"
              :key="category.id"
              class="category-row"
            >
              <div
                class="category-header"
                :class="{ 'is-expanded': isCategoryExpanded(category.id) }"
                @click="toggleCategory(category.id)"
              >
                <i class="el-icon-arrow-right expand-icon" />
                <el-checkbox
                  :value="isCategoryFullySelected(category)"
                  :indeterminate="isCategoryPartiallySelected(category)"
                  class="category-checkbox"
                  @click.native.stop
                  @change="(val) => handleCategorySelectAll(category, val)"
                />
                <span class="category-name">{{ category.name }}</span>
                <el-tooltip
                  v-if="hasPrintedDisciplineInCategory(category)"
                  content="Neki žrebovi u ovoj kategoriji su već štampani"
                  placement="top"
                >
                  <span class="printed-icon">
                    <i class="el-icon-printer" />
                    <i class="el-icon-success printed-icon__check" />
                  </span>
                </el-tooltip>
              </div>
              <div v-show="isCategoryExpanded(category.id)" class="discipline-list">
                <el-checkbox
                  v-for="discipline in category.disciplines"
                  :key="`${category.id}-${discipline.id}`"
                  :value="isDisciplineSelectedInCategory(discipline.id, category.id)"
                  :class="{ 'is-printed': isDisciplinePrinted(discipline.id, category.id) }"
                  class="discipline-checkbox"
                  @change="(val) => toggleDiscipline(discipline.id, category.id, val)"
                >
                  <span class="item-label-wrapper">
                    <span class="item-name">{{ discipline.name }}</span>
                    <el-tooltip
                      v-if="isDisciplinePrinted(discipline.id, category.id)"
                      content="Ovaj žreb je već štampan"
                      placement="top"
                    >
                      <span class="printed-icon">
                        <i class="el-icon-printer" />
                        <i class="el-icon-success printed-icon__check" />
                      </span>
                    </el-tooltip>
                  </span>
                </el-checkbox>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Otkaži</el-button>
      <el-button
        type="primary"
        :disabled="selectedDisciplineIds.length === 0"
        @click="handlePrint"
      >
        Štampaj
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: 'BracketPrintModal',

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    brackets: {
      type: Array,
      default: () => []
    },
    tournamentId: {
      type: [Number, String],
      default: null
    },
    // `categoryId-disciplineId` keys of draws that have already been printed.
    // Passed in by the parent so the warning icons update as soon as a print runs.
    printedPairs: {
      type: Array,
      default: () => []
    },
    // Legacy per-category / per-discipline markers kept for prints made before
    // the pair-based tracking was introduced.
    printedCategoryIds: {
      type: Array,
      default: () => []
    },
    printedDisciplineIds: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      expandedCategoryIds: [],
      selectedDisciplineIds: [],
      // Track selections as categoryId-disciplineId pairs to prevent cross-category leaks
      selectedCategoryDisciplines: new Set(),
      selectAll: false,
      // Map of categoryId -> Set of disciplineIds for that category
      categoryDisciplineMap: new Map()
    }
  },

  computed: {
    dialogVisible: {
      get () {
        return this.visible
      },
      set (val) {
        this.$emit('update:visible', val)
      }
    },

    categoriesWithDisciplines () {
      const categories = this.getUniqueCategories()
      // Rebuild the map completely each time
      this.categoryDisciplineMap.clear()
      const result = categories.map(cat => {
        const disciplines = this.getDisciplinesInCategory(cat.id)
        // Store mapping for quick lookup
        if (disciplines.length > 0) {
          this.categoryDisciplineMap.set(cat.id, new Set(disciplines.map(d => d.id)))
        }
        return {
          ...cat,
          disciplines
        }
      }).filter(cat => cat.disciplines.length > 0)
      return result
    },

    allDisciplineIds () {
      return this.categoriesWithDisciplines.flatMap(cat => cat.disciplines.map(d => d.id))
    },

    isIndeterminate () {
      // Check based on composite keys to get accurate count
      const total = this.categoriesWithDisciplines.reduce((sum, cat) => sum + cat.disciplines.length, 0)
      const selected = this.selectedCategoryDisciplines.size
      return selected > 0 && selected < total
    }
  },

  watch: {
    visible (newVal) {
      if (newVal) {
        this.resetModal()
      }
    },
    selectedDisciplineIds () {
      const total = this.allDisciplineIds.length
      this.selectAll = total > 0 && this.selectedDisciplineIds.length === total
    }
  },

  methods: {
    isDisciplinePrinted (disciplineId, categoryId) {
      if (this.printedPairs.includes(`${categoryId}-${disciplineId}`)) return true
      if (this.printedCategoryIds.includes(categoryId)) return true
      return this.printedDisciplineIds.includes(disciplineId)
    },

    hasPrintedDisciplineInCategory (category) {
      return category.disciplines.some(d => this.isDisciplinePrinted(d.id, category.id))
    },

    resetModal () {
      this.expandedCategoryIds = []
      this.selectedDisciplineIds = []
      this.selectedCategoryDisciplines.clear()
      this.selectAll = false
      this.categoryDisciplineMap.clear()
    },

    isCategoryExpanded (categoryId) {
      return this.expandedCategoryIds.includes(categoryId)
    },

    toggleCategory (categoryId) {
      const idx = this.expandedCategoryIds.indexOf(categoryId)
      if (idx === -1) {
        this.expandedCategoryIds = [...this.expandedCategoryIds, categoryId]
      } else {
        this.expandedCategoryIds = this.expandedCategoryIds.filter(id => id !== categoryId)
      }
    },

    isCategoryFullySelected (category) {
      if (category.disciplines.length === 0) return false
      // Check if all disciplines in this category are selected (using composite keys)
      return category.disciplines.every(d => {
        const key = `${category.id}-${d.id}`
        return this.selectedCategoryDisciplines.has(key)
      })
    },

    isCategoryPartiallySelected (category) {
      // Check how many disciplines in this category are selected (using composite keys)
      const selected = category.disciplines.filter(d => {
        const key = `${category.id}-${d.id}`
        return this.selectedCategoryDisciplines.has(key)
      }).length
      return selected > 0 && selected < category.disciplines.length
    },

    handleCategorySelectAll (category, checked) {
      // category.disciplines already contains only disciplines for this category
      const disciplineIds = category.disciplines.map(d => d.id)

      if (checked) {
        // Add all disciplines for this category using composite keys
        disciplineIds.forEach(disciplineId => {
          const key = `${category.id}-${disciplineId}`
          this.selectedCategoryDisciplines.add(key)
        })
        // Also update the legacy selectedDisciplineIds for backward compatibility
        this.selectedDisciplineIds = [...new Set([...this.selectedDisciplineIds, ...disciplineIds])]
      } else {
        // Remove all disciplines for this category using composite keys
        disciplineIds.forEach(disciplineId => {
          const key = `${category.id}-${disciplineId}`
          this.selectedCategoryDisciplines.delete(key)
        })
        // Also update the legacy selectedDisciplineIds
        // Only remove if no other category has this discipline selected
        disciplineIds.forEach(disciplineId => {
          let hasOtherCategory = false
          for (const [catId, disciplineSet] of this.categoryDisciplineMap.entries()) {
            if (catId !== category.id && disciplineSet.has(disciplineId)) {
              const otherKey = `${catId}-${disciplineId}`
              if (this.selectedCategoryDisciplines.has(otherKey)) {
                hasOtherCategory = true
                break
              }
            }
          }
          if (!hasOtherCategory) {
            this.selectedDisciplineIds = this.selectedDisciplineIds.filter(id => id !== disciplineId)
          }
        })
      }
    },

    isDisciplineSelectedInCategory (disciplineId, categoryId) {
      // Check if discipline is selected in this specific category using composite key
      const key = `${categoryId}-${disciplineId}`
      return this.selectedCategoryDisciplines.has(key)
    },

    toggleDiscipline (disciplineId, categoryId, checked) {
      const key = `${categoryId}-${disciplineId}`

      if (checked) {
        // Add using composite key
        this.selectedCategoryDisciplines.add(key)
        // Also update legacy selectedDisciplineIds
        if (!this.selectedDisciplineIds.includes(disciplineId)) {
          this.selectedDisciplineIds = [...this.selectedDisciplineIds, disciplineId]
        }
      } else {
        // Remove using composite key
        this.selectedCategoryDisciplines.delete(key)
        // Also update legacy selectedDisciplineIds
        // Only remove if no other category has this discipline selected
        let hasOtherCategory = false
        for (const [catId, disciplineSet] of this.categoryDisciplineMap.entries()) {
          if (catId !== categoryId && disciplineSet.has(disciplineId)) {
            const otherKey = `${catId}-${disciplineId}`
            if (this.selectedCategoryDisciplines.has(otherKey)) {
              hasOtherCategory = true
              break
            }
          }
        }
        if (!hasOtherCategory) {
          this.selectedDisciplineIds = this.selectedDisciplineIds.filter(id => id !== disciplineId)
        }
      }
    },

    handleClose () {
      this.dialogVisible = false
      this.$emit('close')
    },

    handleSelectAllChange (val) {
      if (val) {
        // Select all disciplines in all categories using composite keys
        this.categoriesWithDisciplines.forEach(category => {
          category.disciplines.forEach(discipline => {
            const key = `${category.id}-${discipline.id}`
            this.selectedCategoryDisciplines.add(key)
          })
        })
        this.selectedDisciplineIds = [...this.allDisciplineIds]
      } else {
        // Clear all selections
        this.selectedCategoryDisciplines.clear()
        this.selectedDisciplineIds = []
      }
    },

    getUniqueCategories () {
      const categoriesMap = new Map()

      this.brackets.forEach(bracket => {
        if (!bracket.category || !bracket.category.id) return

        const categoryId = bracket.category.id
        const categoryName = bracket.category.name || `Category ${categoryId}`
        const ageFrom = bracket.category.ageFrom || 0

        if (!categoriesMap.has(categoryId)) {
          categoriesMap.set(categoryId, {
            id: categoryId,
            name: categoryName,
            ageFrom: ageFrom
          })
        }
      })

      return Array.from(categoriesMap.values()).sort((a, b) => {
        if (a.ageFrom && b.ageFrom) {
          return a.ageFrom - b.ageFrom
        }
        return a.name.localeCompare(b.name)
      })
    },

    getDisciplinesInCategory (categoryId) {
      const disciplinesMap = new Map()

      this.brackets.forEach(bracket => {
        if (!bracket.category || bracket.category.id !== categoryId) return

        // Handle both individual and team disciplines
        // teamDiscipline can be either an object with id/name or just a number (ID)
        let discipline = bracket.discipline
        if (!discipline && bracket.teamDiscipline) {
          // If teamDiscipline is a number, we need to find the discipline object from another bracket
          if (typeof bracket.teamDiscipline === 'number' || typeof bracket.teamDiscipline === 'string') {
            // Find a bracket with the same teamDiscipline ID that has it as an object
            const teamDisciplineId = Number(bracket.teamDiscipline)
            const referenceBracket = this.brackets.find(b => {
              if (b.teamDiscipline && typeof b.teamDiscipline === 'object' && b.teamDiscipline.id === teamDisciplineId) {
                return true
              }
              return false
            })
            discipline = referenceBracket?.teamDiscipline || { id: teamDisciplineId, name: `Discipline ${teamDisciplineId}` }
          } else {
            discipline = bracket.teamDiscipline
          }
        }

        if (!discipline || !discipline.id) return

        const disciplineId = discipline.id
        const disciplineName = discipline.name || `Discipline ${disciplineId}`

        // Use categoryId-disciplineId as key to ensure uniqueness per category
        const uniqueKey = `${categoryId}-${disciplineId}`
        if (!disciplinesMap.has(uniqueKey)) {
          disciplinesMap.set(uniqueKey, { id: disciplineId, name: disciplineName, categoryId })
        }
      })

      return Array.from(disciplinesMap.values()).sort((a, b) => a.name.localeCompare(b.name))
    },

    buildPrintConfig () {
      // Build selected discipline IDs from composite keys
      const selectedDisciplineIds = []
      const categoryDisciplinePairs = {}

      this.selectedCategoryDisciplines.forEach(key => {
        const [categoryId, disciplineId] = key.split('-')
        if (!categoryDisciplinePairs[categoryId]) {
          categoryDisciplinePairs[categoryId] = []
        }
        categoryDisciplinePairs[categoryId].push(Number(disciplineId))
        if (!selectedDisciplineIds.includes(Number(disciplineId))) {
          selectedDisciplineIds.push(Number(disciplineId))
        }
      })

      if (selectedDisciplineIds.length === 0) return null

      // Check if any categories are fully selected
      const fullySelectedCategoryIds = []
      this.categoriesWithDisciplines.forEach(cat => {
        const categoryKey = cat.id.toString()
        if (categoryDisciplinePairs[categoryKey] &&
            categoryDisciplinePairs[categoryKey].length === cat.disciplines.length) {
          fullySelectedCategoryIds.push(cat.id)
        }
      })

      // If all selected disciplines come from fully selected categories, use category mode
      const totalDisciplinesInSelectedCategories = fullySelectedCategoryIds.reduce((sum, catId) => {
        const cat = this.categoriesWithDisciplines.find(c => c.id === catId)
        return sum + (cat ? cat.disciplines.length : 0)
      }, 0)

      if (fullySelectedCategoryIds.length > 0 &&
          selectedDisciplineIds.length === totalDisciplinesInSelectedCategories) {
        return { type: 'categories', selectedIds: fullySelectedCategoryIds }
      }
      const selectedPairs = Array.from(this.selectedCategoryDisciplines).map(key => {
        const [categoryId, disciplineId] = key.split('-')
        return { categoryId: Number(categoryId), disciplineId: Number(disciplineId) }
      })
      return { type: 'disciplines', selectedPairs }
    },

    handlePrint () {
      const config = this.buildPrintConfig()
      if (!config) return
      this.$emit('print', config)
      this.handleClose()
    }
  }
}
</script>

<style lang="scss" scoped>
.print-modal-content {
  min-height: 300px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.step-content {
  padding: 10px 0;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.step-title {
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.selection-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.select-all-checkbox {
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #dcdfe6;
  font-weight: 600;
}

.category-list {
  max-height: 400px;
  overflow-y: auto;
}

.category-row {
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.category-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  background-color: #fafafa;
  border-radius: 4px;
  margin: 4px 0;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f0f0;
  }

  .expand-icon {
    margin-right: 8px;
    transition: transform 0.2s;
    color: #909399;
    font-size: 14px;
  }

  &.is-expanded .expand-icon {
    transform: rotate(90deg);
  }

  .category-checkbox {
    margin-right: 10px;
    flex-shrink: 0;
  }

  .category-name {
    flex: 1;
    font-size: 14px;
    color: #333;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.discipline-list {
  padding: 4px 0 4px 36px;
  border-left: 2px solid #e4e7ed;
  margin-left: 12px;
  margin-bottom: 4px;
}

.discipline-checkbox {
  display: block;
  margin-left: 0;
  padding: 8px 12px;
  border-bottom: 1px solid #f5f5f5;
  box-sizing: border-box;

  &:last-child {
    border-bottom: none;
  }

  &.is-printed {
    background-color: #f6ffed;
    padding: 8px 12px;
    margin: 4px 0;
    border-radius: 4px;
    border-bottom: 1px solid #b7eb8f;
    box-sizing: border-box;
  }
}

.item-checkbox {
  display: block;
  margin-left: 0;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  box-sizing: border-box;

  &:last-child {
    border-bottom: none;
  }

  &.is-printed {
    background-color: #f6ffed;
    padding: 8px 12px;
    margin: 4px 0;
    border-radius: 4px;
    border-bottom: 1px solid #b7eb8f;
    box-sizing: border-box;
  }
}

.item-label-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.item-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.printed-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #f0f2f5;
  border: 1px solid #dcdfe6;
  color: #303133;
  font-size: 16px;
  line-height: 1;
  margin-left: 8px;
  cursor: help;
  flex-shrink: 0;
}

.printed-icon__check {
  position: absolute;
  right: -4px;
  bottom: -4px;
  font-size: 16px;
  color: #52c41a;
  background: #fff;
  border-radius: 50%;
}

/* Full-width only for select-all and discipline rows; category row checkbox must not take full width */
.select-all-checkbox::v-deep .el-checkbox,
.discipline-checkbox::v-deep .el-checkbox {
  width: 100%;
  box-sizing: border-box;
}

.category-header::v-deep .el-checkbox {
  width: auto;
}

::v-deep .el-checkbox__label {
  font-size: 14px;
  color: #606266;
  box-sizing: border-box;
  overflow: hidden;
}

.select-all-checkbox::v-deep .el-checkbox__label,
.discipline-checkbox::v-deep .el-checkbox__label {
  width: 100%;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
